<template>
  <div
    v-loading="isLoadingPickups"
    class="overflow-scroll"
  >
    <h3 class="mb-1">
      {{ title || 'Pickups Table' }}
    </h3>
    <PickupsTable :pickups="pickups">
      <template v-slot:peek="{ pickup }">
        <PickupPeek :pickup="pickup" />
      </template>

      <template v-slot:reference="{ pickup }">
        <router-link :to="{ name: 'customerservice-pickups-pickup', params: { pickupId: pickup.id } }">
          {{ pickup.booking_reference }}
        </router-link>
      </template>

      <template
        v-if="canUpdate"
        v-slot:actions="{ pickup }"
      >
        <el-dropdown
          split-button
          size="mini"
          trigger="click"
          @command="handleActionCommand"
          @click="canConfirmPickup(pickup) ? confirmPickup(pickup) : reBookPickup(pickup)"
        >
          {{ canConfirmPickup(pickup) ? 'Confirm' : 'Re-book' }}
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item
              :command="{ action: cancelPickup, pickup}"
              :disabled="!canCancelPickup(pickup)"
            >
              Cancel
            </el-dropdown-item>

            <el-dropdown-item
              :command="{ action: holdPickup, pickup}"
              :disabled="!canHoldPickup(pickup)"
            >
              Hold
            </el-dropdown-item>

            <el-dropdown-item
              :command="{ action: processPickup, pickup}"
              :disabled="!canProcessPickup(pickup)"
            >
              Process
            </el-dropdown-item>

            <el-dropdown-item
              :command="{ action: pushPickup, pickup}"
              :disabled="!canPushPickup(pickup)"
            >
              Push
            </el-dropdown-item>

            <el-dropdown-item
              v-if="canConfirmPickup(pickup)"
              :command="{ action: reBookPickup, pickup}"
              divided
            >
              Re-book
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </template>
    </PickupsTable>

    <el-pagination
      class="text-center pt-3"
      :current-page.sync="pickups.page"
      :page-size.sync="pickups.limit"
      layout="total, sizes, prev, pager, next, jumper"
      :total="pickups.total"
      @size-change="reload"
      @current-change="reload"
    />
  </div>
</template>

<script>
import PickupsTable from '@/components/Application/CustomerService/Pickups/Table'
import PickupPeek from '@/components/Application/CustomerService/Pickups/Pickup/Peek'
import { statusAllows } from '@/components/Application/CustomerService/Pickups/Pickup/PickupFunctions'
import PickupCreateDialog from '@/components/Application/CustomerService/Pickups/Pickup/CreateDialog'
import Filter, { IS } from '@/plugins/sdk/Filter'
import { CREATED, UPDATED, DELETED, RESTORED } from '@/plugins/sdk/Events'
import Vue from 'vue'
import { UPDATE } from '@/plugins/user'

export default {
  components: {
    PickupsTable,
    PickupPeek
  },
  props: {
    title: {
      type: String,
      required: false,
      default: 'Pickups Table'
    },
    status: {
      type: Array,
      required: false,
      default: () => []
    },
    type: {
      type: Array,
      required: false,
      default: () => []
    },
    isResizable: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data () {
    return {
      pickups: [],
      isLoadingPickups: true,
      pickupCreateDialogVisible: false,
      pickupCreateDialogInstance: undefined
    }
  },
  computed: {
    canUpdate () {
      return this.$user.can(UPDATE, this.$sdk.pickup)
    },
    filters () {
      const filters = []
      if (!this.$_.isEmpty(this.status)) {
        filters.push(new Filter('status', IS, this.status))
      }
      if (!this.$_.isEmpty(this.type)) {
        filters.push(new Filter('type', IS, this.type))
      }
      return filters
    }
  },
  watch: {
    pickupCreateDialogVisible (visible) {
      if (!visible && typeof this.pickupCreateDialogInstance !== 'undefined') {
        this.pickupCreateDialogInstance.$destroy()
        this.pickupCreateDialogInstance.$el.remove()
        this.pickupCreateDialogInstance = undefined
      }
    }
  },
  created () {
    this
      .$sdk
      .pickup
      .list(request => {
        request.filters = this.filters
        request.include = [
          'account',
          'carrier',
          'carrier_service',
          'profile',
          'items.type',
          'consignments.items.type'
        ]
        request.limit = 10
        request.order = 'created_at:asc'
      })
      .then(res => { this.pickups = res })
      .finally(() => { this.isLoadingPickups = false })
  },
  mounted () {
    this
      .$sdk
      .pickup
      .on([ CREATED, UPDATED, DELETED, RESTORED ], this.reload, this)
  },
  methods: {
    canCancelPickup (pickup) {
      return statusAllows(pickup.status, 'cancelled')
    },
    canHoldPickup (pickup) {
      return statusAllows(pickup.status, 'held')
    },
    canProcessPickup (pickup) {
      return statusAllows(pickup.status, 'pending')
    },
    canPushPickup (pickup) {
      return this.canProcessPickup
    },
    canConfirmPickup (pickup) {
      return statusAllows(pickup.status, 'confirmed')
    },
    handleActionCommand ({ action, pickup }) {
      action(pickup)
    },
    async reload () {
      this.isLoadingPickups = true

      try {
        await this.pickups.reload(request => { request.filters = this.filters })
      } catch {
        this.$message.error('Failed to refresh Pickups')
      }

      this.isLoadingPickups = false
    },
    createPickup (pickup) {
      const PickupCreateDialogClass = Vue.extend(PickupCreateDialog)

      this.pickupCreateDialogInstance = new PickupCreateDialogClass({
        propsData: {
          pickup: pickup
        }
      })

      this
        .pickupCreateDialogInstance
        .$on('destroy', () => {
          this.pickupCreateDialogVisible = false
        })

      this
        .pickupCreateDialogInstance
        .$mount()

      this
        .$root
        .$el
        .appendChild(this.pickupCreateDialogInstance.$el)

      this.pickupCreateDialogVisible = true
    },
    cancelPickup (pickup) {
      this
        .$prompt(
          'Enter a Cancellation Reason',
          `Cancel Pickup ${pickup.booking_reference}`,
          {
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel'
          }
        )
        .then(({ value }) => {
          this
            .$sdk
            .note
            .create({
              content: value,
              resource: 'pickup',
              resource_uuid: pickup.id
            })
            .then(() => {
              pickup = Object.assign(pickup.clone(), {
                status: 'cancelled'
              })

              pickup
                .update()
                .then(res => {
                  this.$message.success(`Updated Pickup ${res.booking_reference}`)
                })
            })
        })
    },
    holdPickup (pickup) {
      pickup = Object.assign(pickup.clone(), {
        status: 'held'
      })

      pickup
        .update()
        .then(res => {
          this.$message.success(`Updated Pickup ${res.booking_reference}`)
        })
    },
    processPickup (pickup) {
      pickup = Object.assign(pickup.clone(), {
        status: 'pending'
      })

      pickup
        .update()
        .then(res => {
          this.$message.success(`Updated Pickup ${res.booking_reference}`)
        })
    },
    pushPickup (pickup) {
      this
        .$alert(
          `Pushing a Pickup will skip OpenFreight validation rules but will not force ${pickup.carrier.name} to accept the Pickup request if the Pickup information is invalid.`,
          'Notice',
          {
            callback: action => {
              if (action === 'confirm') {
                pickup = Object.assign(pickup.clone(), {
                  is_forced: true,
                  status: 'pending'
                })

                pickup
                  .update()
                  .then(res => {
                    this.$message.success(`Updated Pickup ${res.booking_reference}`)
                  })
              }
            }
          }
        )
    },
    confirmPickup (pickup) {
      this
        .$prompt(
          'Enter a Confirmation Reference',
          `Confirm Pickup ${pickup.booking_reference}`,
          {
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel'
          }
        )
        .then(({ value }) => {
          pickup = Object.assign(pickup.clone(), {
            status: 'confirmed',
            carrier_reference: value
          })

          pickup
            .update()
            .then(res => {
              this.$message.success(`Updated Pickup ${res.booking_reference}`)
            })
        })
    },
    async reBookPickup (pickup) {
      pickup = pickup.clone()

      await pickup.refresh(request => {
        request.include = [
          'account.carrier_settings.carrier',
          'account.carrier_service_settings.carrier_service.carrier',
          'account.teams.shipper_setting.address',
          'profile',
          'carrier',
          'carrier_service',
          'sender',
          'receiver',
          'consignments.items.type',
          'items.type',
          'customer_time_zone',
          'team.shipper_setting.address',
          'events.profile'
        ]
      })

      const reBookedPickup = pickup.replicate()

      reBookedPickup.id = undefined
      reBookedPickup.booking_reference = undefined
      reBookedPickup.carrier_reference = undefined
      reBookedPickup.customer_pickup_date = new Date()
      reBookedPickup.profile = undefined
      reBookedPickup.status = undefined
      reBookedPickup.previous_pickup = pickup

      reBookedPickup
        .items
        .map(item => {
          item.id = undefined
          item.created_at = undefined
          item.updated_at = undefined
        })

      reBookedPickup
        .notes
        .map(note => {
          note.id = undefined
          note.created_at = undefined
          note.updated_at = undefined
          note.creator = undefined
        })

      this.createPickup(reBookedPickup)
    }
  }
}
</script>
